import { CircleAlertIcon, RotateCcwIcon } from "lucide-react";
import { useState } from "react";
import { useQuery } from "react-query";
import { getErrorDetail, getErrorMessage } from "#/api/errors";
import { templateVersion } from "#/api/queries/templates";
import type { Workspace } from "#/api/typesGenerated";
import {
	HelpPopover,
	HelpPopoverAction,
	HelpPopoverContent,
	HelpPopoverIconTrigger,
	HelpPopoverLinksGroup,
	HelpPopoverText,
	HelpPopoverTitle,
	HelpPopoverTrigger,
} from "#/components/HelpPopover/HelpPopover";
import { Link } from "#/components/Link/Link";
import { Skeleton } from "#/components/Skeleton/Skeleton";
import { toast } from "#/components/Toaster/toast";
import { linkToTemplate, useLinks } from "#/modules/navigation";
import {
	useWorkspaceUpdate,
	WorkspaceUpdateDialogs,
} from "../WorkspaceUpdateDialogs";

type WorkspaceOutdatedTooltipProps = {
	workspace: Workspace;
	children?: React.ReactNode;
};

export const WorkspaceOutdatedTooltip: React.FC<
	WorkspaceOutdatedTooltipProps
> = ({ workspace, children }) => {
	const [isOpen, setIsOpen] = useState(false);

	// Keep trigger clicks from triggering a parent clickable row's navigation.
	// Radix composes its own click handler, so the popover still opens. The
	// span's key handlers only satisfy Biome's lint/a11y/useKeyWithClickEvents.
	const stopPropagation = (event: React.SyntheticEvent) => {
		event.stopPropagation();
	};

	return (
		<HelpPopover open={isOpen} onOpenChange={setIsOpen}>
			{children ? (
				<HelpPopoverTrigger asChild>
					<span
						className="flex items-center gap-1.5"
						onClick={stopPropagation}
						onKeyDown={stopPropagation}
						onKeyUp={stopPropagation}
					>
						<CircleAlertIcon className="text-content-secondary" size={14} />
						<span>{children}</span>
					</span>
				</HelpPopoverTrigger>
			) : (
				<HelpPopoverIconTrigger
					size="small"
					hoverEffect={false}
					onClick={stopPropagation}
				>
					<CircleAlertIcon className="text-content-secondary" />
					<span className="sr-only">Outdated info</span>
				</HelpPopoverIconTrigger>
			)}
			<WorkspaceOutdatedTooltipContent isOpen={isOpen} workspace={workspace} />
		</HelpPopover>
	);
};

type TooltipContentProps = WorkspaceOutdatedTooltipProps & { isOpen: boolean };

const WorkspaceOutdatedTooltipContent: React.FC<TooltipContentProps> = ({
	workspace,
	isOpen,
}) => {
	const getLink = useLinks();
	const { data: activeVersion } = useQuery({
		...templateVersion(workspace.template_active_version_id),
		enabled: isOpen,
	});
	const updateWorkspace = useWorkspaceUpdate({
		workspace,
		latestVersion: activeVersion,
		onError: (error) => {
			toast.error(
				getErrorMessage(error, `Error updating workspace "${workspace.name}".`),
				{
					description: getErrorDetail(error),
				},
			);
		},
	});

	const versionLink = `${getLink(
		linkToTemplate(workspace.organization_name, workspace.template_name),
	)}`;

	return (
		<>
			<HelpPopoverContent disablePortal={false}>
				<HelpPopoverTitle>Outdated</HelpPopoverTitle>
				<HelpPopoverText className="text-xs font-normal">
					This workspace version is outdated and a newer version is available.
				</HelpPopoverText>

				<div className="flex flex-col gap-2 py-2 text-xs font-normal">
					<div className="leading-[1.6]">
						<div className="text-content-primary text-sm font-semibold">
							New version
						</div>
						<div>
							{activeVersion ? (
								<Link
									href={`${versionLink}/versions/${activeVersion.name}`}
									target="_blank"
									rel="noreferrer"
									size="sm"
									className="p-0"
									showExternalIcon={false}
								>
									{activeVersion.name}
								</Link>
							) : (
								<Skeleton variant="text" height={20} width={100} />
							)}
						</div>
					</div>

					<div className="leading-[1.6]">
						<div className="text-content-primary text-sm font-semibold">
							Message
						</div>
						<div>
							{activeVersion ? (
								activeVersion.message || "No message"
							) : (
								<Skeleton variant="text" height={20} width={150} />
							)}
						</div>
					</div>
				</div>

				<HelpPopoverLinksGroup>
					<HelpPopoverAction
						icon={RotateCcwIcon}
						onClick={updateWorkspace.update}
					>
						Update
					</HelpPopoverAction>
				</HelpPopoverLinksGroup>
			</HelpPopoverContent>
			<WorkspaceUpdateDialogs {...updateWorkspace.dialogProps} />
		</>
	);
};
